<template>
  <div class="docuform-actions">
    <div class="actions-wrap">
      <VcButton v-for="action in buttonActions"
                :key="action.id"
                :color="action.primary ? 'secondary' : 'white'"
                @click="clickHandler(action)"
                :label="$t(action.labelKey)"
                class="docuform-action-button"
                :class="{'docuform-action-button--secondary': !action.primary, 'docuform-action-button--primary': action.primary}"/>
      <VcitaMultiLevelMenu
        :items="dropdownActions"
        :activatorText="this.$t('client.matters.matter_page.docuforms_tab.more')"
        activatorIcon="icon-dots-three-horizontal"
        @click="clickHandler" />
    </div>
  </div>
</template>

<script>
import getActions from '../services/actionsService';
import docuformsService from '../../common/services/docuformsService';
import VcitaMultiLevelMenu from '../../common/components/VcitaMultiLevelMenu/VcitaMultiLevelMenu';
import { showSuccess, showError } from '@/modules/common/services/frontageToastService.js';

export default {
  name: "DocuformActions",
  components: { VcitaMultiLevelMenu },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
    isInfected: {
      type: Boolean,
      default: false,
    },
    contactUid: {
      type: String,
      required: true,
    },
    contactName: {
      type: String,
      required: true,
    },
    matterUid: {
      type: String,
      required: true,
    },
    downloadSrc: {
      type: String,
      default: '',
    },
    accountCanSendLink: {
      type: Boolean,
      default: false,
    }
  },
  computed: {
    actions() {
      return getActions({
        status: this.item.status,
        isMobile: this.isMobile,
        includeIcons: false,
        isInfected: this.isInfected,
        canReUpload: this.canReUpload,
        canSendRequest: this.canSendRequest,
      });
    },
    canReUpload() {
      return this.$hasFeature('documents_reupload')
    },
    buttonActions() {
      return this.actions.buttons
    },
    dropdownActions() {
      return this.actions.dropdown.map(a => { return ({...a, text: this.$t(a.labelKey) })})
    },
    isClientInitiated() {
      return this.item.initiator == 'Client';
    },
    canSendRequest(){
      return this.$hasFeature('documents_reupload_portal')
        && this.accountCanSendLink
        && this.isClientInitiated
        && this.isInfected;
    },
  },
  methods: {
    clickHandler (item) {
      if (!item.id || !this[item.id] || typeof this[item.id] !== 'function') return;
      this[item.id]();
      this.$track('Document activity page action', {
        action: item.id
      })
    },
    print() {
      document.getElementById("docuform-iframe").contentWindow.postMessage('print-document');
    },
    download() {
      if (!this.item.metadata?.file_location || !this.downloadSrc) return;
      this.$frontageApi.openExternal(this.downloadSrc);
    },
    reupload() {
      this.$frontageApi.send('infected-file', this.item);
    },
    viewConversation() {
      if (this.isMobile) {
        this.$frontageApi.navigateTo('app.client', {
          tab: 'conversation',
          uid: this.contactUid,
          matter_uid: this.matterUid
        })
      } else {
        this.$root.$emit('docuform-matter-pane-conversation');
      }
    },
    viewDocuments() {
      this.$frontageApi.navigateTo('app.documents');
    },
    delete() {
      docuformsService.deleteItem(this.item.uid)
        .then(res => {
          this.viewDocuments();
          showSuccess(this.$t('client.matters.matter_page.docuforms_tab.delete_success'));
        })
        .catch(error => {
          showError(this.$t('client.matters.matter_page.docuforms_tab.delete_failure'));
      });
    },
    share() {
      docuformsService.shareItem(this.item.uid)
        .then(res => {
          this.$store.dispatch({type: "mattersModule/loadDocuform", payload: { uid: res.document }})
          this.viewConversation();
          showSuccess(this.$t('client.matters.matter_page.docuforms_tab.share_success'));
        })
        .catch(error => {
          showError(this.$t('client.matters.matter_page.docuforms_tab.share_failure'));
        });
    },
    viewSignature() {
      this.$frontageApi.send('view-signature', {entityType: 'docuform_activity', entityUid: this.item.uid, contactName: this.contactName});
    },
    sendRequest(){
      this.$frontageApi.send('send-file-request-action', this.item)
    },
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.docuform-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;

  .actions-wrap {
    height: 32px;

    @include md-and-up-main {
      margin-inline-start: 24px;
      padding-inline-end: 24px;
    }
  }

  button.docuform-action-button {
    font-size: 13px;
    font-weight: 500;
    margin-inline-end: 12px;
    padding: 0 10px;
    height: 32px;

    &--secondary {
      color: $buttonBlackTextColor;
    }

    &--primary {
      font-weight: 600;
    }

    @include md-and-up-main {
      padding: 0 20px;
    }
  }

  ::v-deep .multi-menu__activator {
    color: $buttonBlackTextColor;
    background-color: $core-neutral-400;
    border-radius: 5px;
    width: auto;
    padding: 0 16px 0 12px;
    height: 32px;

    span {
      .v-icon {
        font-size: 16px;
      }

      .multi-menu__activator-text {
        letter-spacing: 0.039px;
        font-weight: 500;
        margin-inline-start: 2px;
      }
    }
  }

  ::v-deep .VcButton.docuform-action-button--secondary {
    background-color: $core-neutral-400!important;
  }
}
</style>
